import { SyntheticEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useNotifications } from 'app/application/application'
import { Connection, PagedTable, SearchInput } from 'app/common/presentation'
import { DeleteIcon } from 'app/common/presentation/icons'
import { useConnections } from 'app/connections/application'
import { InstalledConnection } from 'app/connections/types'

import { Button, DropDown, IconButton, Modal, Status, Table } from 'components'

import { mapToUrl } from '../../connectionUrlMapper'
import { translateStatus } from '../../translateStatus'
import styles from './InstalledConnections.module.scss'

const InstalledConnections = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('connections')
  const { createNotification } = useNotifications()
  const { deleteConnection: deleteConnection, connections: connections } = useConnections()

  const statusFilters = useMemo(
    () => [
      {
        label: t('overview.installed.filter.all'),
        value: 'all',
      },
      {
        label: t('overview.installed.filter.active'),
        value: 'active',
      },
      {
        label: t('overview.installed.filter.inactive'),
        value: 'disabled',
      },
    ],
    [t],
  )

  const [statusFilter, setStatusFilter] = useState(statusFilters[0].value)
  const [search, setSearch] = useState('')
  const [connectionToDelete, setConnectionToDelete] = useState<InstalledConnection>()

  const handleDelete =
    (conn: InstalledConnection, confirmed = false) =>
    (e: SyntheticEvent) => {
      e.stopPropagation()

      if (confirmed) {
        void deleteConnection(conn)
          .then((result) => {
            if (result.ok) {
              createNotification({
                message: t('delete.success.message', {
                  name: t(`available.${conn.name}.displayName`),
                }),
                title: t('delete.success.title'),
                type: 'success',
              })
            } else {
              createNotification({
                message: result.val.message,
                title: t(`http.${result.val.code}`),
                type: 'error',
              })
            }
          })
          .finally(() => setConnectionToDelete(undefined))
      } else {
        setConnectionToDelete(conn)
      }
    }

  return (
    <>
      <div className={styles.toolbar}>
        <DropDown
          className={styles.filter}
          data={statusFilters}
          onSelect={setStatusFilter}
          selected={statusFilter}
        />
        <SearchInput
          className={styles.search}
          onChange={setSearch}
          placeholder={t('overview.installed.search')}
          value={search}
        />
      </div>

      <PagedTable
        className={styles.table}
        columns={[
          t('overview.installed.table.connection'),
          t('overview.installed.table.type'),
          t('overview.installed.table.status'),
          '',
        ]}
        data={connections.filter((conn) => statusFilter === 'all' || conn.status === statusFilter)}
        renderRow={(conn, index) => (
          <Table.Row
            key={index}
            onClick={() => navigate(`/connections/${mapToUrl(conn.name)}/setup`)}
          >
            <Table.Cell>
              <Connection connection={conn} />
            </Table.Cell>
            <Table.Cell>{t(`category.${conn.category}`)}</Table.Cell>
            <Table.Cell>
              <Status status={translateStatus(conn.status, conn.error)}>
                {t(`status.${conn.status}`)}
              </Status>
            </Table.Cell>
            <Table.Cell>
              <IconButton
                className={styles.delete}
                icon={DeleteIcon}
                onClick={handleDelete(conn)}
                variant="tertiary"
              />
            </Table.Cell>
          </Table.Row>
        )}
        useSearchParamsPaging
      />

      {connectionToDelete && (
        <Modal
          actions={
            <>
              <Button onClick={() => setConnectionToDelete(undefined)} variant="secondary">
                {t('delete.cancel')}
              </Button>
              <Button
                destructive
                onClick={handleDelete(connectionToDelete, true)}
                variant="primary"
              >
                {t('delete.confirm')}
              </Button>
            </>
          }
          className={styles.modal}
          contentClassName={styles.content}
          onClose={() => setConnectionToDelete(undefined)}
          onSubmit={handleDelete(connectionToDelete, true)}
          open={true}
          title={t('delete.title')}
        >
          <div>
            {t('delete.description', {
              connectionName: t(`available.${connectionToDelete.name}.displayName`),
            })}
          </div>
        </Modal>
      )}
    </>
  )
}

export default InstalledConnections
